import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ExternalLink,
  Heart,
  Bed,
  Bath,
  Ruler,
  MapPin,
  Calendar,
  Trash2,
  Car,
  Dog,
  Sofa,
  Shirt,
  User,
  Phone,
  Mail,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { RENTAL } from "@/constants/testIds";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1627141440602-e9c1e5fd2fbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODAwOTkyMzV8MA&ixlib=rb-4.1.0&q=85";

const EDITABLE_FIELDS = [
  "notes",
  "contact_name",
  "contact_phone",
  "contact_email",
];

const draftFromRental = (rental) =>
  EDITABLE_FIELDS.reduce((acc, k) => {
    acc[k] = rental?.[k] || "";
    return acc;
  }, {});

export const RentalDetailModal = ({ rental, onClose, onFav, onDelete, onUpdate }) => {
  const [draft, setDraft] = useState(() => draftFromRental(rental));
  const [saving, setSaving] = useState(false);

  // Re-sync the editable fields whenever a different rental is opened.
  useEffect(() => {
    setDraft(draftFromRental(rental));
  }, [rental?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(
    () => EDITABLE_FIELDS.some((k) => (rental?.[k] || "") !== draft[k]),
    [rental, draft],
  );

  if (!rental) return null;

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const rentStr =
    rental.rent != null ? `$${Math.round(rental.rent).toLocaleString()}` : "—";

  const save = async () => {
    if (!dirty || !onUpdate) return;
    setSaving(true);
    try {
      await onUpdate(rental.id, draft);
      toast.success("Changes saved");
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!rental} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid={RENTAL.detailModal}
        className="max-w-4xl p-0 overflow-hidden bg-[#F9F8F6] border-stone-200"
      >
        <DialogTitle className="sr-only">{rental.title}</DialogTitle>
        <div className="grid md:grid-cols-5 max-h-[88vh]">
          <div className="md:col-span-3 bg-stone-100 relative">
            <img
              src={rental.image_url || FALLBACK_IMG}
              alt={rental.title}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
              className="w-full h-full object-cover aspect-[4/3] md:aspect-auto md:min-h-[420px]"
            />
            <button
              onClick={() => onFav?.(rental)}
              className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm"
              aria-label="Favorite"
            >
              <Heart
                size={18}
                strokeWidth={1.7}
                className={
                  rental.favorite
                    ? "text-[#C25934] fill-[#C25934]"
                    : "text-stone-600"
                }
              />
            </button>
          </div>

          <div className="md:col-span-2 p-7 overflow-y-auto">
            <p className="overline">{rental.city || "Listing"}</p>
            <h2 className="font-heading text-3xl font-medium tracking-tight mt-1 leading-tight">
              {rental.title || "Untitled rental"}
            </h2>

            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading text-5xl font-medium text-[#4A5D4E] leading-none">
                {rentStr}
              </span>
              <span className="text-stone-500">/month</span>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-stone-700">
              <Tile icon={<Bed size={15} />} label="Bedrooms" value={rental.bedrooms ?? "—"} />
              <Tile icon={<Bath size={15} />} label="Bathrooms" value={rental.bathrooms ?? "—"} />
              <Tile
                icon={<Ruler size={15} />}
                label="Sqft"
                value={rental.sqft != null ? Math.round(rental.sqft) : "—"}
              />
            </div>

            <div className="mt-5 space-y-2.5 text-sm text-stone-700">
              {rental.address && (
                <Row icon={<MapPin size={14} />} text={rental.address} />
              )}
              {rental.available_date && (
                <Row
                  icon={<Calendar size={14} />}
                  text={`Available ${rental.available_date}`}
                />
              )}
              {rental.garage && <Row icon={<Car size={14} />} text="Garage" />}
              {rental.parking && !rental.garage && (
                <Row icon={<Car size={14} />} text="Parking available" />
              )}
              {rental.pet_friendly && (
                <Row icon={<Dog size={14} />} text="Pet friendly" />
              )}
              {rental.furnished && (
                <Row icon={<Sofa size={14} />} text="Furnished" />
              )}
              {rental.laundry && (
                <Row icon={<Shirt size={14} />} text={`Laundry: ${rental.laundry}`} />
              )}
            </div>

            {rental.description && (
              <div className="mt-5">
                <p className="overline mb-2">About</p>
                <p className="text-sm text-stone-700 leading-relaxed">
                  {rental.description}
                </p>
              </div>
            )}

            <div className="mt-5">
              <Label className="overline mb-2 block">Your Notes</Label>
              <Textarea
                data-testid={RENTAL.detailNotes}
                value={draft.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Private notes, pros & cons, questions to ask…"
                rows={3}
                className="bg-white"
              />
            </div>

            <div className="mt-5">
              <p className="overline mb-2">Contact</p>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <User size={15} className="text-stone-400 shrink-0" />
                  <Input
                    data-testid={RENTAL.detailContactName}
                    value={draft.contact_name}
                    onChange={(e) => setField("contact_name", e.target.value)}
                    placeholder="Landlord / agent name"
                    className="bg-white h-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={15} className="text-stone-400 shrink-0" />
                  <Input
                    type="tel"
                    data-testid={RENTAL.detailContactPhone}
                    value={draft.contact_phone}
                    onChange={(e) => setField("contact_phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-white h-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={15} className="text-stone-400 shrink-0" />
                  <Input
                    type="email"
                    data-testid={RENTAL.detailContactEmail}
                    value={draft.contact_email}
                    onChange={(e) => setField("contact_email", e.target.value)}
                    placeholder="name@example.com"
                    className="bg-white h-9"
                  />
                </div>
              </div>
            </div>

            {dirty && (
              <div className="mt-4">
                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid={RENTAL.detailSave}
                  className="w-full bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6]"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </div>
            )}

            <div className="mt-7 flex flex-wrap items-center gap-3">
              {rental.source_url && (
                <a
                  href={rental.source_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={RENTAL.detailSourceLink}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A5D4E] hover:text-[#3D4D40] underline-offset-4 hover:underline"
                >
                  <ExternalLink size={14} />
                  Open original ad
                </a>
              )}
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete?.(rental)}
                data-testid={RENTAL.detailDelete}
                className="text-stone-500 hover:text-[#C25934]"
              >
                <Trash2 size={14} className="mr-1.5" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ icon, label, value }) => (
  <div className="bg-white border border-stone-200 rounded-xl px-3 py-3 flex flex-col gap-1">
    <span className="text-stone-400">{icon}</span>
    <span className="overline">{label}</span>
    <span className="font-heading text-xl font-medium text-stone-900 leading-none">
      {value}
    </span>
  </div>
);

const Row = ({ icon, text }) => (
  <div className="flex items-center gap-2 text-stone-700">
    <span className="text-stone-400">{icon}</span>
    {text}
  </div>
);

export default RentalDetailModal;
